import CardImage from './CardImage';
import CardList from './CardList';
import { CardContentFragmentFragment } from '../../../__generated__/graphql';
import ButtonLink from '../../../components/Button/ButtonLink';
import { ButtonColor } from '../../../components/Button/types';
import RawHtml from '../../../components/RawHtml';
import { Heading2, Heading3, Paragraph } from '../../../components/Text';
import { TextProps } from '../../../components/Text/TextBase';

interface CardContentProps {
  cardContent: CardContentFragmentFragment;
  hasCardHeading: boolean;
  isDarkCardBackground: boolean;
}

type CardContentElementType =
  | 'Button'
  | 'Disclaimer'
  | 'Heading'
  | 'Image'
  | 'List'
  | 'Paragraph'
  | 'Title';

const getFontColor = (props: CardContentProps): TextProps['fontColor'] => {
  if (!props.isDarkCardBackground) {
    return 'black';
  }

  if (props.hasCardHeading) {
    return 'white';
  }

  return 'turquoise';
};

const CardContent = ({
  cardContent,
  hasCardHeading,
  isDarkCardBackground,
}: CardContentProps) => {
  if (cardContent.elementType == null) {
    return null;
  }

  switch (cardContent.elementType[0] as CardContentElementType | null) {
    case 'Button':
      return (
        <ButtonLink
          buttonColor={(cardContent.buttonColor as ButtonColor | null | undefined) ?? undefined}
          id={cardContent.buttonId ?? undefined}
          linkFragment={cardContent.button}
        />
      );
    case 'Heading':
      return (
        <Heading2
          fontColor={isDarkCardBackground ? 'turquoise' : 'black'}
          fontSizeOverride={38}
          fontWeight={300}
        >
          {cardContent.heading}
        </Heading2>
      );
    case 'Image':
      return (
        <CardImage
          alignment={cardContent.imageData?.alignment as 'center' | 'left' | undefined}
          caption={cardContent.imageData?.caption}
          image={cardContent.imageData?.image}
        />
      );
    case 'List':
      return <CardList cardContent={cardContent} />;
    case 'Paragraph':
      return (
        <Paragraph fontSize={15}>
          {cardContent.paragraph}
        </Paragraph>
      );
    case 'Title':
      return (
        <Heading3
          fontColor={getFontColor({
            cardContent,
            hasCardHeading,
            isDarkCardBackground,
          })}
          fontSizeOverride={24}
        >
          {cardContent.title}
        </Heading3>
      );
    case 'Disclaimer':
      if (!cardContent.disclaimer) {
        return null;
      }

      return (
        <RawHtml>
          {cardContent.disclaimer}
        </RawHtml>
      );
    case null:
    default:
      return null;
  }
};

export default CardContent;
